/*
 * Company: WHIZ Ltd.
 * Project: TA
 * Module: WebApplication
 * Author: sales@whiz.bg
 */

const STRING_EMPTY = "";
const STRING_NOT_AVAILABLE = "N/A";
const STRING_BGN = "BGN";
const STRING_EUR = "EUR";
const STRING_XBUL = "XBUL";
const STRING_XFRA = "XFRA";
const STRING_XETR = "XETR";

/**
 * Formats a number to specific locale (en-US)
 * @param {Number} num 
 * @param {Number} afterDot 
 * @returns String
 */
function formatNumber(num, afterDot = 2) {
    if (null === num || "undefined" === typeof num || STRING_EMPTY === num) {
        return STRING_EMPTY;
    }

    const numericValue = Number(num);
    if (!Number.isFinite(numericValue)) {
        return STRING_EMPTY;
    }

    const parsedDigits = parseInt(afterDot);
    const fractionDigits = Number.isInteger(parsedDigits) && 0 <= parsedDigits
        ? parsedDigits
        : 2;

    return numericValue.toLocaleString("en-US", {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
}

function getMarketPrice(stockItem) {
    return stockItem.ldp
        ? stockItem.ldp
        : stockItem.clp
            ? stockItem.clp
            : stockItem.mp;
}

function getCurrencyUnitsPerEurRate(userWallet, currency) {
    const normalizedCurrency = currency?.toString().toUpperCase();
    if (!normalizedCurrency) {
        return 0;
    }
    if (STRING_EUR === normalizedCurrency) {
        return 1;
    }

    const rate = userWallet?.r?.find(item => {
        const rateCurrency = item.c?.toString().toUpperCase().split("/")[0];
        return rateCurrency === normalizedCurrency;
    });

    return rate ? parseFloat(rate.r) : 0;
}

function convertCurrencyViaEurRate(userWallet, amount, sourceCurrency, targetCurrency) {
    const sourceAmount = parseFloat(amount);
    const normalizedSourceCurrency = sourceCurrency?.toString().toUpperCase();
    const normalizedTargetCurrency = targetCurrency?.toString().toUpperCase();

    if (!Number.isFinite(sourceAmount) || !normalizedSourceCurrency || !normalizedTargetCurrency) {
        return 0;
    }
    if (normalizedSourceCurrency === normalizedTargetCurrency) {
        return sourceAmount;
    }

    const sourceUnitsPerEurRate = getCurrencyUnitsPerEurRate(userWallet, normalizedSourceCurrency);
    const targetUnitsPerEurRate = getCurrencyUnitsPerEurRate(userWallet, normalizedTargetCurrency);

    if (0 === sourceUnitsPerEurRate || 0 === targetUnitsPerEurRate) {
        return 0;
    }

    return sourceAmount / sourceUnitsPerEurRate * targetUnitsPerEurRate;
}

function getMoneyInCurrency(userWallet, amount, sourceCurrency, targetCurrency) {
    const sourceAmount = toFiniteNumber(amount, 0);
    const normalizedSourceCurrency = sourceCurrency?.toString().toUpperCase();
    const normalizedTargetCurrency = targetCurrency?.toString().toUpperCase();

    if (!normalizedSourceCurrency || !normalizedTargetCurrency) {
        return 0;
    }
    if (normalizedSourceCurrency === normalizedTargetCurrency) {
        return sourceAmount;
    }

    return sourceAmount * getCurrencyRatio(userWallet, normalizedSourceCurrency, normalizedTargetCurrency);
}

function firstFinitePositiveNumber(...values) {
    for (const value of values) {
        const numericValue = toFiniteNumber(value, 0);
        if (0 < numericValue) {
            return numericValue;
        }
    }

    return 0;
}

function getExchangeCode(item, stockInfo = null) {
    return (
        item?.xcode ??
        item?.xc ??
        item?.ex ??
        item?.mic ??
        stockInfo?.xcode ??
        stockInfo?.xc ??
        stockInfo?.ex ??
        stockInfo?.mic ??
        STRING_EMPTY
    ).toString().toUpperCase();
}

function getRawBondInitialNominal(item, stockInfo = null) {
    return firstFinitePositiveNumber(
        item?.innom,
        item?.initialNominal,
        item?.initialnominal,
        stockInfo?.innom,
        stockInfo?.initialNominal,
        stockInfo?.initialnominal
    );
}

function getBondNominal(item, stockInfo = null) {
    const initialNominal = getRawBondInitialNominal(item, stockInfo);
    const exchangeCode = getExchangeCode(item, stockInfo);
    if (
        2 === parseInt(item?.stid) &&
        0 < initialNominal &&
        [STRING_XFRA, STRING_XETR].includes(exchangeCode)
    ) {
        return initialNominal;
    }

    const nominal = firstFinitePositiveNumber(
        item?.nom,
        item?.nominal,
        stockInfo?.nom,
        stockInfo?.nominal
    );

    return 0 === nominal || 1 === nominal ? 1000.0 : nominal;
}

function getBondInitialNominal(item, stockInfo = null) {
    const nominal = getBondNominal(item, stockInfo);
    const initialNominal = getRawBondInitialNominal(item, stockInfo);

    return 0 === initialNominal ? nominal : initialNominal;
}

function getBondDisplayFaceValue(item, rawAmount) {
    const amount = toFiniteNumber(rawAmount, 0);
    if (0 === amount) {
        return 0;
    }

    return amount;
}

function getBondFaceValueStep(item, exchangeCode, stockInfo = null) {
    const initialNominal = getBondInitialNominal(item, stockInfo);
    if (0 === STRING_XFRA.localeCompare(exchangeCode?.toString().toUpperCase())) {
        return initialNominal / 10.0;
    }

    return initialNominal;
}

function getBondMoneyPrincipal(item, rawAmount, stockInfo = null) {
    const displayFaceValue = getBondDisplayFaceValue(item, rawAmount, stockInfo);
    if (0 === displayFaceValue) {
        return 0;
    }

    const nominal = getBondNominal(item, stockInfo);
    const initialNominal = getBondInitialNominal(item, stockInfo);
    return displayFaceValue * nominal / initialNominal;
}

function getBondMoneyValue(item, rawAmount, pricePercent, stockInfo = null) {
    return getBondMoneyPrincipal(item, rawAmount, stockInfo) * toFiniteNumber(pricePercent, 0) / 100.0;
}

function getPercentPricedMoneyValue(rawAmount, pricePercent) {
    return toFiniteNumber(rawAmount, 0) * toFiniteNumber(pricePercent, 0) / 100.0;
}

function getInstrumentMoneyValue(item, rawAmount, price, stockInfo = null) {
    if (parseInt(item?.stid) === 2) {
        return getBondMoneyValue(item, rawAmount, price, stockInfo);
    }
    if (parseInt(item?.stid) === 3) {
        return getPercentPricedMoneyValue(rawAmount, price);
    }

    return toFiniteNumber(rawAmount, 0) * toFiniteNumber(price, 0);
}

function getPositionMoneyValue(stocks, indexedStocks, userWallet, item, currency, price) {
    if (!item) {
        return 0;
    }

    const itemCurrency = item.cc?.toString().toUpperCase();
    const stockInfo = getStock(stocks, indexedStocks, item.ex, item.sc);
    return getMoneyInCurrency(
        userWallet,
        getInstrumentMoneyValue(item, item.am, price, stockInfo),
        itemCurrency,
        currency
    );
}

/**
 * Calcualtes total amount of money in user's wallet depending on selected currency
 * @param {UserWalletDTO} userWallet 
 * @param {String} currency 
 * @returns Number
 */
function getTotalMoney(userWallet, currency) {
    if (!userWallet) {
        return 0;
    }
    let total = 0;
    const targetCurrency = currency?.toString().toUpperCase();
    userWallet.m?.forEach(item => {
        total += convertCurrencyViaEurRate(userWallet, item.am_ori, item.c, targetCurrency);
    });

    return total;
}

function getExchangeIdByCode(exchanges, code) {
    let toRet = 0;
    if (!exchanges || 0 === exchanges?.length || !code) {
        return toRet;
    }
    const BreakException = {};
    try {
        exchanges.forEach(elem => {
            if (elem.code &&
                0 === code.toString().toUpperCase().localeCompare(elem.code.toString().toUpperCase())) {
                toRet = parseInt(elem.eID);
                // cannot break forEach
                throw BreakException;
            }
        });
    } catch (error) {
        if (BreakException !== error) {
            console.error(error);
        }
    }

    return toRet;
}

function getExchangeCodeById(exchanges, id) {
    if (!exchanges || 0 === exchanges?.length || !id) {
        return STRING_EMPTY;
    }
    const exchange = exchanges.filter(item => parseInt(item.eID) === parseInt(id));
    return !exchange || 0 === exchange?.length ? STRING_EMPTY : exchange[0].code;
}

function getExchangeCurrencyById(exchanges, id) {
    if (!exchanges || 0 === exchanges?.length || !id) {
        return STRING_EMPTY;
    }
    const exchange = exchanges.filter(item => parseInt(item.eID) === parseInt(id));
    return !exchange || 0 === exchange?.length ? STRING_EMPTY : exchange[0].cc;
}

function getExchangeCurrencyByCode(exchanges, code) {
    if (!exchanges || 0 === exchanges?.length || !code) {
        return STRING_EMPTY;
    }
    const exchange = exchanges.filter(item => 0 === code.toUpperCase().localeCompare(item.code.toUpperCase()));
    return !exchange || 0 === exchange?.length ? STRING_EMPTY : exchange[0].cc;
}

function getIndexCurrencyByName(exchanges, code) {
    if (!exchanges || 0 === exchanges?.length || !code) {
        return STRING_EMPTY;
    }
    const exchange = exchanges.filter(item => 0 !== item.indexes.filter(index => 0 === index.n.toUpperCase().localeCompare(code.toUpperCase())).length);
    return !exchange || 0 === exchange?.length ? STRING_EMPTY : exchange[0].cc;
}

/**
 * Returns stock info based on stock code and exchange ID
 * @param {*} stocks 
 * @param {*} exchangeCode 
 * @param {*} stockCode 
 * @returns 
 */
function getStock(stocks, indexedStocks, exchangeCode, stockCode) {
    if (!stocks || 0 === stocks?.length) {
        return null;
    }
    if (!indexedStocks || 0 === indexedStocks?.length) {
        return null;
    }
    if (!stockCode) {
        return null;
    }
    if (!exchangeCode) {
        return null;
    }
    // get stock index by stock code
    const stockIndex = indexedStocks.indexOf(exchangeCode + stockCode);
    if (-1 === stockIndex) {
        return null;
    }
    // get by index as indexes should match
    const stock = stocks[stockIndex];
    return stock ? stock : null;
}

/**
 * Searches and return the name of stocks based on its stock code and exchange ID
 * @param {*} stocks 
 * @param {*} stockCode 
 * @param {*} exchangeCode 
 * @param {*} language 
 * @returns String
 */
function getStockName(stocks, indexedStocks, stockCode, exchangeCode, language) {
    let toRet = STRING_NOT_AVAILABLE;
    const stock = getStock(stocks, indexedStocks, exchangeCode, stockCode);
    if (stock) {
        if (0 === "BG".localeCompare(language.toString().toUpperCase())) {
            toRet = stock.nBG ? stock.nBG : stock.nEN;
        } else {
            toRet = stock.nEN;
        }
    }

    return toRet;
}

function getStockCurrency(stocks, indexedStocks, stockCode, exchangeCode) {
    const stock = getStock(stocks, indexedStocks, exchangeCode, stockCode);

    return stock
        ? stock.cc.toString().toUpperCase()
        : STRING_NOT_AVAILABLE;
}

/**
 * Returns specific currency rate from wallet data
 * @param {*} userWallet 
 * @param {*} currency 
 * @returns float
 */
function getCurrencyRate(userWallet, currency) {
    // eur is base currency as of 2026-01-01
    if (currency === 'eur' || currency === 'EUR') return 1;
    if (currency === 'bgn' || currency === 'BGN') return 0.51129;

    if (!userWallet) {
        return 0;
    }
    // calculate for BGN
    const rate = userWallet.r?.filter(item => {
        return 0 === item.c.toString().toUpperCase().localeCompare(currency.toString().toUpperCase() + "/" + STRING_BGN);
    });
    if (!rate || 0 === rate?.length) {
        return 0;
    }

    return 1 / parseFloat(rate[0].r);
}

function createExchangeStockCodePairObject(exchangeCode, stockCode) {
    return {
        xc: exchangeCode,
        sc: stockCode
    };
}

function combineExchangeStockPairFromObject(pair) {
    return pair.xc + pair.sc;
}

function combineExchangeStockPair(exchangeCode, stockCode) {
    return exchangeCode + stockCode;
}

function extractExchangeCodeFromPairObject(pair) {
    return pair && pair.xc ? pair.xc : null;
}

function extractStockCodeFromPairObject(pair) {
    return pair && pair.sc && null !== pair.sc ? pair.sc : null;
}

function compareExchangeStockPairs(pair1, pair2) {
    // must be valid
    if (!pair1) {
        return false;
    }
    if (!pair2) {
        return false;
    }
    try {
        return 0 === pair1.xc.toString().toUpperCase().localeCompare(pair2.xc.toString().toUpperCase()) &&
            0 === pair1.sc.toString().toUpperCase().localeCompare(pair2.sc.toString().toUpperCase())
    } catch (error) {
        console.error(error);
        return false;
    }
}

function extractStocksInWatchlist(stocks, watchlist, exchanges) {
    const mappedStocks = [];
    try {
        if (!stocks || 0 === stocks?.length) {
            return mappedStocks;
        }
        if (!exchanges || 0 === exchanges?.length) {
            return mappedStocks;
        }
        if (!watchlist || !watchlist.sc || 0 === watchlist?.sc?.length) {
            return mappedStocks;
        }
        const BreakException = {};
        watchlist.sc.forEach(pair => {
            try {
                const xCodeId = getExchangeIdByCode(exchanges, pair.xc);
                stocks.forEach(elem => {
                    if (elem.code &&
                        0 === pair.sc.toString().toUpperCase().localeCompare(elem.code.toString().toUpperCase()) &&
                        parseInt(xCodeId) === parseInt(elem.cMIC)) {
                        mappedStocks.push(elem);
                        // cannot break forEach
                        throw BreakException;
                    }
                });
            } catch (error) {
                if (BreakException !== error) {
                    console.error(error);
                }
            }
        });
    } catch (error) {
        console.error(error);
    }

    return mappedStocks;
}

function verifyPairListContainsElement(list, pair) {
    if (!list || 0 === list?.length) {
        return false;
    }
    if (!pair) {
        return false;
    }
    try {
        const pairInList = list.filter(elem => {
            return 0 === pair.sc.toString().toUpperCase().localeCompare(elem.sc.toString().toUpperCase()) &&
                0 === pair.xc.toString().toUpperCase().localeCompare(elem.xc.toString().toUpperCase());
        });
        return pairInList && 0 < pairInList?.length;
    } catch (error) {
        console.error(error);
    }

    return false;
}

function getUserWatchlists(user) {
    const watchlistsToDisplay = [];
    // add default
    user?.wls?.forEach(wl => {
        if (0 === 'DEFAULT'.localeCompare(wl.wln.toString().toUpperCase())) {
            watchlistsToDisplay.push(wl);
        }
    });
    // add all others
    user?.wls?.forEach(wl => {
        if (0 !== 'DEFAULT'.localeCompare(wl.wln.toString().toUpperCase())) {
            watchlistsToDisplay.push(wl);
        }
    });
    return watchlistsToDisplay;
}

function getIndexedWatchlists(exchanges, indexesWatchlists, selectedExchange) {
    // filter by exchange
    if (0 !== parseInt(selectedExchange)) {
        // XFRA should display XETR indexes
        const ex = 3 === parseInt(selectedExchange)
            ? 2
            : parseInt(selectedExchange);
        const filteredIndexesWatchlist = [];
        for (let cnt = 0; cnt < exchanges.length; cnt++) {
            for (let exCnt = 0; exCnt < exchanges[cnt].indexes.length; exCnt++) {
                for (let wlsCnt = 0; wlsCnt < indexesWatchlists.length; wlsCnt++) {
                    if (0 === indexesWatchlists[wlsCnt].wln.toString().toUpperCase().localeCompare(exchanges[cnt].indexes[exCnt].n.toString().toUpperCase())) {
                        if (ex === parseInt(exchanges[cnt].eID)) {
                            filteredIndexesWatchlist.push(indexesWatchlists[wlsCnt]);
                            break;
                        }
                    }
                }
            }
        }
        return filteredIndexesWatchlist;
    }
    return indexesWatchlists;
}

function isInt(n) {
    return Number(n) === n && n % 1 === 0;
}

function isFloat(n) {
    return Number(n) === n && n % 1 !== 0;
}

function parseUnixMsTime(itemTime) {
    if (!itemTime) {
        return STRING_EMPTY;
    }
    try {
        const itemDate = new Date(itemTime);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const year = itemDate.getFullYear();
        const month = months[itemDate.getMonth()];
        const date = "0" + itemDate.getDate();
        const hour = "0" + itemDate.getHours();
        const min = "0" + itemDate.getMinutes();
        const sec = "0" + itemDate.getSeconds();
        const time = date.toString().slice(-2) + ' ' + month + ' ' + year.toString().slice(-2) + ' ' + hour.toString().slice(-2) + ':' + min.toString().slice(-2) + ':' + sec.toString().slice(-2);
        return time;
    } catch (error) {
        // do nothing
    }
    return STRING_EMPTY;
}

function parseUnixTime(itemTime) {
    if (!itemTime) {
        return STRING_EMPTY;
    }
    return parseUnixMsTime(itemTime * 1000);
}

/**
 * Sorts array by date field
 * @param {*} items 
 * @param {*} dateType 
 * @param {*} isMs 
 * @returns array
 */
function sortByDate(items, dateType, isMs) {
    return items.sort(function (a, b) {
        if (a[dateType] && b[dateType]) {
            const date1 = true === isMs ? new Date(a[dateType]) : new Date(a[dateType] * 1000);
            const date2 = true === isMs ? new Date(b[dateType]) : new Date(b[dateType] * 1000);
            return date1.getTime() - date2.getTime();
        }
        return 0;
    });
}

/**
 * Sorts an array by a string property asc 
 * @param {*} items 
 * @param {*} fieldName 
 * @returns array
 */
function sortByStringProperty(items, fieldName) {
    return items.sort((a, b) => a[fieldName].localeCompare(b[fieldName]));
}

/**
 * Sorts an array by a double property asc 
 * @param {*} items 
 * @param {*} fieldName 
 * @returns array
 */
function sortByDoubleProperty(items, fieldName) {
    return items.sort((a, b) => parseFloat(a[fieldName]) - parseFloat(b[fieldName]));
}

function formatPrice(val, curr, userLang, autoDigits = null) {
    // Defensive defaults
    if (!curr) {
        curr = "";
    }
    if (!userLang) {
        userLang = "EN";
    }
    if (isNaN(parseFloat(val))) {
        val = 0;
    }

    const currString = curr.toString();
    const currUpper = currString.toUpperCase();
    const isCurrencyValue = STRING_EMPTY !== currString && "%" !== currString;

    let afterDot;
    if (null === autoDigits || "undefined" === typeof autoDigits) {
        afterDot = isCurrencyValue ? 2 : calculateNumberOfMeaningfulDigits(val);
        if (!isCurrencyValue && 0 === val) {
            afterDot = 0;
        }
    } else {
        afterDot = Number(parseInt(autoDigits));
    }

    // detect negativity and format absolute value only
    const isNegative = Number(val) < 0;
    const absVal = Math.abs(Number(val));
    const formattedNumber = formatNumber(absVal, afterDot);
    const sign = isNegative ? "-" : "";

    if (0 === currUpper.localeCompare(STRING_BGN)) {
        return sign + STRING_BGN + " " + formattedNumber;
    }

    // No currency
    if ("" === currString) {
        return sign + formattedNumber;
    }

    switch (currUpper) {
        case "EUR":
            return sign + "€" + formattedNumber;
        case "USD":
            return sign + "$" + formattedNumber;
        case "JPY":
            return sign + "¥" + formattedNumber;
        case "CHF":
            return sign + "₣" + formattedNumber;
        case "GBP":
            return sign + "£" + formattedNumber;
    }

    // Default
    return sign + currUpper + " " + formattedNumber;
}

function findQuote(store, stockCode, exchangeCode) {
    const quotes = store.getters["getQuotes"];
    const matchedItems = quotes.filter(item => {
        return 0 === item.xc.toString().toUpperCase().localeCompare(exchangeCode.toString().toUpperCase())
            && 0 === item.sc.toString().toUpperCase().localeCompare(stockCode.toString().toUpperCase())
    });
    return matchedItems ? matchedItems[0] : null;
}

function calculateNumberOfMeaningfulDigits(quotePrice) {
    let precision = 0;
    if (isNaN(parseFloat(quotePrice)) || 0 === parseFloat(quotePrice)) {
        return precision;
    }
    if (10.0 > parseFloat(quotePrice)) {
        precision = 4;
    } else if (100.0 > parseFloat(quotePrice)) {
        precision = 3;
    } else if (1000.0 > parseFloat(quotePrice)) {
        precision = 2;
    } else if (10000.0 > parseFloat(quotePrice)) {
        precision = 1;
    }

    return precision;
}

function getCurrencyRatio(userWallet, stockCurrency, requestedCurrency) {
    const stockCurrencyRate = getCurrencyRate(userWallet, stockCurrency);
    const requestedCurrencyRate = getCurrencyRate(userWallet, requestedCurrency);
    if (0 === stockCurrencyRate || 0 === requestedCurrencyRate) {
        return 0;
    }

    return stockCurrencyRate / requestedCurrencyRate;
}

function extractExchangeEquitiesCount(exchange, userWallet) {
    // no wallet, no stocks in wallet
    if (!userWallet || !userWallet.s) {
        return 0;
    }
    // get stocks for explicit exchange
    const exchangeStocks = userWallet.s.filter(item => {
        return 0 === item?.ex?.toUpperCase().localeCompare(exchange.toUpperCase());
    });
    // no shares in this exchange
    if (!exchangeStocks) {
        return 0;
    }

    // calculate assets in requested exchange
    let assetsInCurrency = 0;
    exchangeStocks.forEach(item => {
        assetsInCurrency += item.am;
    });

    return assetsInCurrency;
}

function extractExchangePositionsCount(exchange, userWallet) {
    // no wallet, no stocks in wallet
    if (!userWallet || !userWallet.s) {
        return 0;
    }
    // get stocks for explicit exchange
    const exchangeStocks = userWallet.s.filter(item => {
        return 0 === item?.ex?.toUpperCase().localeCompare(exchange.toUpperCase())
            && 0 < item?.am;
    });
    // no shares in this exchange
    if (!exchangeStocks) {
        return 0;
    }

    return exchangeStocks.length;
}

function getTotalInstrumentsCountByType(type, exchange, userWallet) {
    // no wallet, no stocks in wallet
    if (!userWallet || !userWallet.s) {
        return 0;
    }
    // get stocks for explicit exchange
    const exchangeStocks = userWallet.s.filter(item => {
        return 0 === item?.ex?.toUpperCase().localeCompare(exchange.toUpperCase());
    });
    // no shares in this exchange
    if (!exchangeStocks) {
        return 0;
    }
    let assetsCount = 0;
    exchangeStocks.forEach(element => {
        if (parseInt(type) === element.stid) {
            assetsCount += element.am;
        }
    });
    return assetsCount;
}

function getTotalEquitiesInBuySettlementAsMoney(userWallet, currency) {
    if (!userWallet) {
        return 0;
    }
    let sttlBuyInBgn = 0;
    let sttlBuyInEur = 0;

    userWallet.s?.forEach(item => {
        if (0 === STRING_EUR.localeCompare(item.cc.toString().toUpperCase())) {
            sttlBuyInEur += item.sttl_buy || 0;
        } else if (0 === STRING_BGN.localeCompare(item.cc.toString().toUpperCase())) {
            sttlBuyInBgn += item.sttl_buy || 0;
        }
    });

    const eur2bgn = getCurrencyRate(userWallet, STRING_EUR);

    return 0 === STRING_BGN.localeCompare(currency.toUpperCase())
        ? sttlBuyInBgn + sttlBuyInEur * eur2bgn
        : sttlBuyInEur + sttlBuyInBgn / eur2bgn;
}

function getTotalEquitiesInSellSettlementAsMoney(userWallet, currency) {
    if (!userWallet) {
        return 0;
    }
    let sttlSellInBgn = 0;
    let sttlSellInEur = 0;

    userWallet.s?.forEach(item => {
        if (0 === STRING_EUR.localeCompare(item.cc.toString().toUpperCase())) {
            sttlSellInEur += Math.abs(item.sttl_sell || 0);
        } else if (0 === STRING_BGN.localeCompare(item.cc.toString().toUpperCase())) {
            sttlSellInBgn += Math.abs(item.sttl_sell || 0);
        }
    });

    const eur2bgn = getCurrencyRate(userWallet, STRING_EUR);

    return 0 === STRING_BGN.localeCompare(currency.toUpperCase())
        ? sttlSellInBgn + sttlSellInEur * eur2bgn
        : sttlSellInEur + sttlSellInBgn / eur2bgn;
}

function toFiniteNumber(value, fallback = 0) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : fallback;
}

function getOrderField(item, ...keys) {
    for (const key of keys) {
        if (item?.[key] !== undefined && item?.[key] !== null) {
            return item[key];
        }
    }

    return undefined;
}

function getOrderExchangeCode(item) {
    const exchangeCode = getOrderField(item, "mic", "xcode", "xc", "exchange", "ex");
    return exchangeCode ? exchangeCode.toString().toUpperCase() : STRING_EMPTY;
}

function getOrderStockCode(item) {
    const stockCode = getOrderField(item, "code", "sc", "stockCode");
    return stockCode ? stockCode.toString().toUpperCase() : STRING_EMPTY;
}

function getOrderStockInfo(stocks, indexedStocks, item) {
    return getStock(stocks, indexedStocks, getOrderExchangeCode(item), getOrderStockCode(item));
}

function getOrderQuote(quotes, item) {
    const exchangeCode = getOrderExchangeCode(item);
    const stockCode = getOrderStockCode(item);
    if (!quotes || !exchangeCode || !stockCode) {
        return null;
    }

    return quotes.find(quote => {
        return 0 === quote?.xc?.toString().toUpperCase().localeCompare(exchangeCode)
            && 0 === quote?.sc?.toString().toUpperCase().localeCompare(stockCode);
    }) || null;
}

function getTotalEquitiesAsMoneyV2(stocks, indexedStocks, userWallet, currency) {
    if (!userWallet) {
        return 0;
    }

    let total = 0;
    userWallet.s?.forEach(item => {
        total += getPositionMoneyValue(stocks, indexedStocks, userWallet, item, currency, getMarketPrice(item));
    });

    return total;
}

function getExchangeEquitiesAsMoneyV2(stocks, indexedStocks, userWallet, currency, exchangeCode) {
    if (!userWallet) {
        return 0;
    }

    let total = 0;
    userWallet.s?.forEach(item => {
        if (0 === exchangeCode.localeCompare(item?.ex?.toString().toUpperCase())) {
            total += getPositionMoneyValue(stocks, indexedStocks, userWallet, item, currency, getMarketPrice(item));
        }
    });

    return total;
}

function getXbulEquitiesAsMoneyV2(stocks, indexedStocks, userWallet, currency) {
    return getExchangeEquitiesAsMoneyV2(stocks, indexedStocks, userWallet, currency, STRING_XBUL);
}

function getXetrEquitiesAsMoneyV2(userWallet, currency) {
    return getExchangeEquitiesAsMoneyV2(null, null, userWallet, currency, STRING_XETR);
}

function getXfraEquitiesAsMoneyV2(userWallet, currency) {
    return getExchangeEquitiesAsMoneyV2(null, null, userWallet, currency, STRING_XFRA);
}

function extractExchangeEquitiesValueV2(stocks, indexedStocks, exchangeCode, currency, userWallet) {
    return getExchangeEquitiesAsMoneyV2(stocks, indexedStocks, userWallet, currency, exchangeCode?.toString().toUpperCase());
}

function extractExchangeEquitiesAverageValueV2(stocks, indexedStocks, exchange, currency, userWallet) {
    if (!userWallet) {
        return 0;
    }

    let total = 0;
    userWallet.s?.forEach(item => {
        if (0 === exchange?.toString().toUpperCase().localeCompare(item?.ex?.toString().toUpperCase())) {
            const price = 0 === toFiniteNumber(item.ap, 0)
                ? getMarketPrice(item)
                : item.ap;
            total += getPositionMoneyValue(stocks, indexedStocks, userWallet, item, currency, price);
        }
    });

    return total;
}

function getTradePurchasePriceV2(stocks, indexedStocks, item) {
    if (!item) {
        return 0;
    }

    const price = 0 === toFiniteNumber(item.ap, 0) ? item.mp : item.ap;
    const stockInfo = getStock(stocks, indexedStocks, item.ex, item.sc);
    return getInstrumentMoneyValue(item, item.am, price, stockInfo);
}

function getTradeMarketPriceV2(stocks, indexedStocks, item) {
    if (!item) {
        return 0;
    }

    const price = getMarketPrice(item);
    const stockInfo = getStock(stocks, indexedStocks, item.ex, item.sc);
    return getInstrumentMoneyValue(item, item.am, price, stockInfo);
}

function getTradeTotalAmountValueV2(stocks, indexedStocks, item) {
    if (!item) {
        return 0;
    }

    if (![2, 3].includes(parseInt(item.stid))) {
        return item.pt;
    }

    const stockInfo = getStock(stocks, indexedStocks, item.xc || item.ex, item.sc);
    return getInstrumentMoneyValue(item, item.qty, item.sp, stockInfo);
}

function getOrderEstimatedAmountAsMoneyV2(item, stockInfo = null) {
    if (!item) {
        return 0;
    }

    const amount = toFiniteNumber(getOrderField(item, "cnt", "amount"), 0);
    const price = toFiniteNumber(getOrderField(item, "pr", "price"), 0);

    return getInstrumentMoneyValue(item, amount, price, stockInfo);
}

function isMarketOrderType(item) {
    const type = getOrderField(item, "type", "ot");
    const normalizedType = String(type ?? "").toUpperCase();
    return "1" === normalizedType || "MARKET" === normalizedType;
}

function getMarketOrderEstimatedAmountAsMoney(item, stockInfo = null, quoteInfo = null) {
    const amount = toFiniteNumber(getOrderField(item, "cnt", "amount"), 0);
    const quoteSource = quoteInfo || stockInfo || {};
    const action = String(getOrderField(item, "act", "action") ?? "").toUpperCase();
    const ask = firstFinitePositiveNumber(quoteSource?.a, quoteSource?.ask, quoteSource?.acp, item?.ask, item?.a);
    const bid = firstFinitePositiveNumber(quoteSource?.b, quoteSource?.bid, quoteSource?.bcp, item?.bid, item?.b);
    const lastDeal = firstFinitePositiveNumber(
        quoteSource?.ldp,
        quoteSource?.ltp,
        quoteSource?.lp,
        quoteSource?.lastDealPrice,
        item?.ldp,
        item?.ltp
    );
    const close = firstFinitePositiveNumber(
        quoteSource?.clp,
        quoteSource?.prc,
        quoteSource?.closePrice,
        item?.clp,
        item?.prc
    );
    const quotePrice = 0 === "S".localeCompare(action) ? bid : ask;
    const storedPrice = firstFinitePositiveNumber(
        getOrderField(item, "pr", "price")
    );
    const price = firstFinitePositiveNumber(quotePrice, lastDeal, close, storedPrice);

    if (0 === price) {
        return { amount: 0, source: "none" };
    }

    return {
        amount: getInstrumentMoneyValue(item, amount, price, stockInfo),
        source: 0 < quotePrice ? "quote" : "fallback"
    };
}

function getGuaranteeDepositV2(userWallet, currency, stocks = null, indexedStocks = null, quotes = null) {
    if (!userWallet) {
        return 0;
    }

    let total = 0;
    userWallet.orders?.forEach(item => {
        if (!item) {
            return;
        }

        const act = String(getOrderField(item, "act", "action") ?? "").toUpperCase();
        if (0 !== "B".localeCompare(act)) {
            return;
        }

        const fee = Number(item?.fee ?? 0);
        const stockInfo = getOrderStockInfo(stocks, indexedStocks, item);
        const quoteInfo = getOrderQuote(quotes, item);
        let calc = 0;
        if (isMarketOrderType(item)) {
            const marketEstimate = getMarketOrderEstimatedAmountAsMoney(item, stockInfo, quoteInfo);
            calc = "quote" === marketEstimate.source
                ? marketEstimate.amount * 1.02 + fee
                : "fallback" === marketEstimate.source
                    ? marketEstimate.amount * 4 + fee
                    : 0;
        } else {
            calc = getOrderEstimatedAmountAsMoneyV2(item, stockInfo) + fee;
        }
        total += getMoneyInCurrency(userWallet, calc, item?.cc, currency);
    });

    return total;
}

function getPositionsAmountV2(stocks, indexedStocks, item) {
    if (!item) {
        return 0;
    }
    if (parseInt(item.stid) !== 2) {
        return item.am;
    }

    const stockInfo = getStock(stocks, indexedStocks, item.ex, item.sc);
    return getBondDisplayFaceValue(item, item.am, stockInfo);
}

export default {
    formatNumber: formatNumber,
    getMoneyInCurrency: getMoneyInCurrency,
    getTotalMoney: getTotalMoney,
    getTotalEquitiesAsMoney: getTotalEquitiesAsMoneyV2,
    getXbulEquitiesAsMoney: getXbulEquitiesAsMoneyV2,
    getXetrEquitiesAsMoney: getXetrEquitiesAsMoneyV2,
    getXfraEquitiesAsMoney: getXfraEquitiesAsMoneyV2,
    getExchangeIdByCode: getExchangeIdByCode,
    getExchangeCodeById: getExchangeCodeById,
    getExchangeCurrencyById: getExchangeCurrencyById,
    getExchangeCurrencyByCode: getExchangeCurrencyByCode,
    getIndexCurrencyByName: getIndexCurrencyByName,
    getStock: getStock,
    getStockName: getStockName,
    getCurrencyRate: getCurrencyRate,
    createExchangeStockCodePairObject: createExchangeStockCodePairObject,
    combineExchangeStockPairFromObject: combineExchangeStockPairFromObject,
    combineExchangeStockPair: combineExchangeStockPair,
    extractExchangeCodeFromPairObject: extractExchangeCodeFromPairObject,
    extractStockCodeFromPairObject: extractStockCodeFromPairObject,
    compareExchangeStockPairs: compareExchangeStockPairs,
    extractStocksInWatchlist: extractStocksInWatchlist,
    verifyPairListContainsElement: verifyPairListContainsElement,
    getUserWatchlists: getUserWatchlists,
    getIndexedWatchlists: getIndexedWatchlists,
    isInt: isInt,
    isFloat: isFloat,
    parseUnixTime: parseUnixTime,
    parseUnixMsTime: parseUnixMsTime,
    sortByDate: sortByDate,
    sortByStringProperty: sortByStringProperty,
    sortByDoubleProperty: sortByDoubleProperty,
    formatPrice: formatPrice,
    findQuote: findQuote,
    calculateNumberOfMeaningfulDigits: calculateNumberOfMeaningfulDigits,
    extractExchangeEquitiesValue: extractExchangeEquitiesValueV2,
    extractExchangeEquitiesCount: extractExchangeEquitiesCount,
    extractExchangePositionsCount: extractExchangePositionsCount,
    extractExchangeEquitiesAverageValue: extractExchangeEquitiesAverageValueV2,
    getTotalInstrumentsCountByType: getTotalInstrumentsCountByType,
    getTradePurchasePrice: getTradePurchasePriceV2,
    getTradeMarketPrice: getTradeMarketPriceV2,
    getTradeTotalAmountValue: getTradeTotalAmountValueV2,
    getTotalEquitiesInBuySettlementAsMoney: getTotalEquitiesInBuySettlementAsMoney,
    getTotalEquitiesInSellSettlementAsMoney: getTotalEquitiesInSellSettlementAsMoney,
    getCurrencyRatio: getCurrencyRatio,
    getGuaranteeDeposit: getGuaranteeDepositV2,
    getOrderEstimatedAmountAsMoney: getOrderEstimatedAmountAsMoneyV2,
    getStockCurrency: getStockCurrency,
    getPositionsAmount: getPositionsAmountV2,
    getBondDisplayFaceValue: getBondDisplayFaceValue,
    getBondFaceValueStep: getBondFaceValueStep,
    getBondMoneyValue: getBondMoneyValue
}
